"use client";

import { useMemo, useState } from "react";
import type { ColumnDef, PaginationState, SortingState } from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";

import { AdminDialog } from "@/components/admin/admin-dialog-layout";
import { AdminPaginatedTable } from "@/components/admin/admin-paginated-table";
import { adminTableSerialColumn } from "@/components/admin/admin-table-column-presets";
import { AdminTableSearchField } from "@/components/admin/admin-table-filter";
import {
  ADMIN_TABLE_SERIAL_FIELD_PROPS,
  adminTableIconActionsColumnMeta,
  adminTableSelectColumnMeta,
  resolveAdminTableSortParams,
} from "@/components/admin/admin-table-utils";
import {
  CONVERSATION_MANAGER_KEY,
  ConversationMenu,
  ConversationTitleTip,
  type ConversationAction,
} from "@/components/ai/ai-conversation-actions";
import {
  colFlex,
  colWidth,
  dataTableMeta,
} from "@/components/ui/data-table-column-width";
import { SimpleSelect } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api-client";
import { CONVERSATION_RANGES, type ConversationRange } from "@/lib/ai/chat-ui";
import { formatDateTime } from "@/lib/datetime/display";

type ConversationRow = {
  id: string;
  title: string;
  updatedAt: string;
  shared: boolean;
  agent: { id: string; name: string } | null;

  serial: number;
};

const PAGE_SIZE = 8;

const DEFAULT_SORTING: SortingState = [{ id: "serial", desc: true }];

type ListPayload = {
  items: ConversationRow[];
  total: number;
};

export function AiConversationManager({
  open,
  onOpenChange,
  onOpenConversation,
  onAction,
  agentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  onOpenConversation: (id: string) => void;
  onAction: (action: ConversationAction) => void;
  agentId?: string;
}) {
  const t = useTranslations("member.aiChat.history");
  const ui = useTranslations("site.ui");
  const locale = useLocale();
  const [range, setRange] = useState<ConversationRange>("all");
  const [search, setSearch] = useState("");
  const keyword = search.trim();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const { order } = resolveAdminTableSortParams(sorting, DEFAULT_SORTING);

  const listQuery = useQuery({
    queryKey: [
      ...CONVERSATION_MANAGER_KEY,
      agentId ?? "all",
      range,
      keyword,
      order,
      pagination.pageIndex,
      pagination.pageSize,
    ],
    enabled: open,
    queryFn: ({ signal }) =>
      apiFetch<ListPayload>(
        `/api/member/ai/conversations/list?${new URLSearchParams({
          ...(agentId ? { agentId } : {}),
          range,
          keyword,
          order,
          page: String(pagination.pageIndex + 1),
          pageSize: String(pagination.pageSize),
        })}`,
        { signal },
      ),
  });

  const items = listQuery.data?.items ?? [];

  function resetPage() {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }

  const columns = useMemo<ColumnDef<ConversationRow, unknown>[]>(
    () => [
      {
        id: "select",
        meta: { ...adminTableSelectColumnMeta(), pin: "left" as const },
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
      },
      adminTableSerialColumn<ConversationRow>({ strategy: "field" }),
      {
        id: "title",
        header: t("columnTitle"),
        meta: dataTableMeta(colFlex(240), { className: "px-3" }),
        enableSorting: false,
        cell: ({ row }) => (
          <ConversationTitleTip
            conversation={row.original}
            side="top"
            align="start"
            trigger={
              <button
                type="button"
                className="block max-w-full truncate text-left transition-colors hover:text-brand"
                onClick={() => onOpenConversation(row.original.id)}
              >
                {row.original.title || t("untitled")}
              </button>
            }
          />
        ),
      },
      {
        id: "updatedAt",
        header: t("columnUpdatedAt"),
        meta: dataTableMeta(colWidth(150), { className: "px-3" }),
        enableSorting: false,
        cell: ({ row }) => formatDateTime(row.original.updatedAt, locale),
      },
      {
        id: "actions",
        header: "",
        meta: adminTableIconActionsColumnMeta(1),
        enableSorting: false,
        cell: ({ row }) => (
          <ConversationMenu conversation={row.original} onAction={onAction} className="size-7" />
        ),
      },
    ],
    [t, locale, onOpenConversation, onAction],
  );

  return (
    <AdminDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("title")}
      size="xl"
      cancel={false}
    >
      {listQuery.isError && items.length > 0 ? <div role="alert" className="text-sm text-destructive">{listQuery.error.message}<Button variant="link" onClick={() => void listQuery.refetch()}>{ui("retry")}</Button></div> : null}
      <AdminPaginatedTable
        {...ADMIN_TABLE_SERIAL_FIELD_PROPS}
        columns={columns}
        data={items}
        manualSorting
        sorting={sorting}

        onSortingChange={(next) => setSorting(next.length > 0 ? next : DEFAULT_SORTING)}
        manualPagination
        rowCount={listQuery.data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageSize={PAGE_SIZE}
        pageSizeOptions={[PAGE_SIZE, 15, 30, 100]}
        fixedBodyRows={PAGE_SIZE}
        isPending={listQuery.isFetching}
        emptyState={listQuery.isError ? <div role="alert" className="text-sm text-destructive">{listQuery.error.message}<Button variant="link" onClick={() => void listQuery.refetch()}>{ui("retry")}</Button></div> : undefined}
        tableMinWidth={560}
        onBatchDelete={(ids) => {
          if (ids.length > 0) onAction({ type: "delete", ids });
        }}

        selectionResetToken={listQuery.dataUpdatedAt}
        filters={
          <div className="flex w-full items-center gap-2">
            <SimpleSelect
              value={range}
              onValueChange={(next) => {
                setRange(next as ConversationRange);
                resetPage();
              }}
              className="w-28 shrink-0"
              align="start"
              alignItemWithTrigger={false}
              options={CONVERSATION_RANGES.map((value) => ({
                value,
                label: t(`range.${value}`),
              }))}
            />
            <AdminTableSearchField
              value={search}
              debounceMs={300}
              placeholder={t("searchPlaceholder")}
              className="max-w-none"
              onChange={(next) => {
                setSearch(next);
                resetPage();
              }}
            />
          </div>
        }
      />
    </AdminDialog>
  );
}
